/* global themeVars */

/**
 * Shared go_fw bridge: chains window.goReady callbacks and dedupes
 * concurrent getSessionDetails() calls so header/widgets don't race.
 */
const callbacks = window.__wombatGoReadyCallbacks || ( window.__wombatGoReadyCallbacks = [] );

/**
 * Upper bound (ms) for postponing the GO loader when window load is slow.
 */
const DEFERRED_LOAD_TIMEOUT = 3000;

/**
 * Injects the GO widgets loader script once per page.
 *
 * Pages with widgets need GO right away. Elsewhere only the header reads the
 * session, so the loader (which pulls all vendor chunks, ~1.4 MB) waits for
 * window load or DEFERRED_LOAD_TIMEOUT, whichever comes first, to keep it from
 * competing with the page's own images and styles.
 *
 * @param {Object}  [options]       Loading options.
 * @param {boolean} [options.defer] Postpone injection until load or timeout.
 * @return {void}
 */
export const loadGoFramework = ( { defer = false } = {} ) => {

	const scriptUrl = themeVars?.goWidgetsUrl;

	if ( ! scriptUrl ) {

		return;

	}

	/**
	 * Appends the loader script unless an earlier call already did.
	 *
	 * @return {void}
	 */
	const inject = () => {

		if ( window.__wombatGoLoaderInjected ) {

			return;

		}

		window.__wombatGoLoaderInjected = true;

		const script = document.createElement( 'script' );
		script.src = scriptUrl;
		document.head.append( script );

	};

	if ( ! defer || document.readyState === 'complete' ) {

		inject();

		return;

	}

	window.addEventListener( 'load', inject, { once: true } );
	setTimeout( inject, DEFERRED_LOAD_TIMEOUT );

};

/**
 * Queues a callback to run once GO framework signals readiness.
 *
 * @param {Function} callback Callback to run when GO is ready.
 * @return {void}
 */
export const onGoReady = ( callback ) => {

	callbacks.push( callback );

	if ( callbacks.length > 1 ) {

		return;

	}

	const previousGoReady = typeof window.goReady === 'function' ? window.goReady : null;

	window.goReady = () => {

		previousGoReady?.();
		callbacks.forEach( ( cb ) => cb() );

	};

};

/**
 * Reads the current GO session, deduping concurrent calls into one request.
 *
 * @return {Promise<Object|null>} Session object, or null when GO is unavailable.
 */
export const getGoSession = () => {

	// eslint-disable-next-line no-undef, camelcase
	if ( typeof go_fw === 'undefined' ) {

		return Promise.resolve( null );

	}

	if ( ! window.__wombatGoSessionRequest ) {

		// eslint-disable-next-line no-undef, camelcase
		window.__wombatGoSessionRequest = go_fw.getSessionDetails().finally( () => {

			window.__wombatGoSessionRequest = null;

		} );

	}

	return window.__wombatGoSessionRequest;

};
